"use client";

import { useState } from "react";
import { signIn } from "next-auth/react";
import LoadingDots from "@/components/loading-dots";
import toast from "react-hot-toast";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { TextInput } from "@/components/form/TextInput";
import { Button } from "@/components/actions/Button";
import { wait } from "@/lib/helpers";

export default function RegisterForm({
  plan,
  redirectTo,
}: {
  plan?: string | null;
  redirectTo?: string | null;
}) {
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const router = useRouter();

  const handleGoogleSignUp = async () => {
    setGoogleLoading(true);

    // Store plan in sessionStorage so we can retrieve it after OAuth redirect
    if (plan === "pro") {
      sessionStorage.setItem("pendingPlan", "pro");
    }

    const redirectUrl = redirectTo || (plan === "pro" ? "/app/checkout?plan=intro" : "/app/history");

    await signIn("google", {
      callbackUrl: redirectUrl,
    });
  };

  return (
    <div className="grid gap-6 px-4 py-8 sm:px-16">
      {/* Google Sign Up Button */}
      <Button
        onClick={handleGoogleSignUp}
        disabled={googleLoading || loading}
        type="outline"
        className="w-full"
      >
        {googleLoading ? (
          <LoadingDots color="#808080" />
        ) : (
          <span className="flex items-center justify-center gap-3">
            <svg
              className="svg"
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
            >
              <path
                fill="#4285f4"
                fillOpacity="1"
                fillRule="evenodd"
                stroke="none"
                d="M17.64 9.2q-.002-.956-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
              />
              <path
                fill="#34a853"
                fillOpacity="1"
                fillRule="evenodd"
                stroke="none"
                d="M9.003 18c2.43 0 4.467-.806 5.956-2.18l-2.909-2.26c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18"
              />
              <path
                fill="#fbbc05"
                fillOpacity="1"
                fillRule="evenodd"
                stroke="none"
                d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042z"
              />
              <path
                fill="#ea4335"
                fillOpacity="1"
                fillRule="evenodd"
                stroke="none"
                d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.002 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71"
              />
            </svg>
            Continue with Google
          </span>
        )}
      </Button>

      <div className="flex items-center justify-center text-sm text-gray-400">
        OR
      </div>

      {/* Email/Password Form */}
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setLoading(true);

          try {
            // Step 1: Create account
            const resp = await fetch("/api/auth/register", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                name,
                email,
                password,
                plan: plan || "free", // Pass plan to backend
              }),
            });

            if (!resp.ok) {
              const errorData = await resp.json();
              throw new Error(errorData.error || "Failed to create account");
            }

            const registrationData = await resp.json();

            await wait(300);

            // Step 2: Sign in
            const result = await signIn("credentials", {
              email,
              password,
              redirect: false,
            });

            if (result?.error) {
              toast.error(result.error);
              setError("Error logging in");
              setLoading(false);
              return;
            }

            // Step 3: Redirect based on plan
            if (redirectTo) {
              router.push(redirectTo);
            } else if (plan === "pro" && !registrationData.hasPaidAccess) {
              router.push("/app/checkout?plan=intro"); // Redirect to checkout for paid plan
            } else {
              router.push("/app/history"); // Free users go straight to app
            }
          } catch (err: any) {
            toast.error(err.message || "Something went wrong");
            setError(err.message || "Error creating account");
            setLoading(false);
          }
        }}
        className="grid gap-4"
      >
        <TextInput
          name="name"
          type="text"
          placeholder="John Doe"
          autoComplete="name"
          required
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          theme="light"
        />
        <TextInput
          name="email"
          type="email"
          placeholder="person@thinknado.com"
          autoComplete="email"
          required
          label="Email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          theme="light"
        />
        <TextInput
          name="password"
          type="password"
          placeholder="********"
          autoComplete="password"
          required
          label="Password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          theme="light"
        />

        {error && <p className="text-red-500 text-sm text-center">{error}</p>}

        <Button color="bg-yellow" disabled={loading || googleLoading} className="login-button">
          {loading ? (
            <LoadingDots color="#808080" />
          ) : (
            <span>{plan === "pro" ? "Continue to Checkout" : "Join Now"}</span>
          )}
        </Button>

        {plan === "pro" && (
          <p className="text-xs text-center text-gray-400">
            You'll complete payment after creating your account
          </p>
        )}
      </form>

      <p className="text-center text-sm text-white">
        Have an account?{" "}
        <Link
          href={redirectTo ? `/auth/sign-in?redirect=${encodeURIComponent(redirectTo)}` : "/auth/sign-in"}
          className="font-semibold text-white"
        >
          Sign in
        </Link>
        .
      </p>
    </div>
  );
}
